import React, { useEffect, useState } from "react";
import {
  Card,
  Table,
  Select,
  InputNumber,
  Button,
  message,
  Popconfirm,
  Row,
  Col,
  Input,
  Tooltip,
} from "antd";
import { FormattedMessage, useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import OpenNotificationWithIcon from "CommonComponents/ResultNotification/Notification";
import { useParams } from "react-router-dom";
import { getProductsServicesAPI } from "features/ProductsandServices/ProductsandServicesAPI";
import {
  GetWorkOrderItemsByWorkOrderIdApi,
  AddWorkOrderItemApi,
  UpdateWorkOrderItemApi,
  DeleteWorkOrderItemApi,
} from "../ClientsApi";
import {
  getAppointmentItemsList,
  addAppointmentItem,
  updateAppointmentItem,
  deleteAppointmentItem,
} from "features/Clients/Appointments/AppointmentApi";
import { EditOutlined, PlusOutlined, DeleteOutlined } from "@ant-design/icons";

const { Option } = Select;

function WorkOrderProducts(props) {
  const intl = useIntl();
  const dispatch = useDispatch();
  const { workOrderId } = useParams();
  const workOrderItems = useSelector((state) =>
    props.appointmentId
      ? state.appointments.appointmentItemsList || []
      : state.clients.workOrderItemList?.items || []
  );
  const totalPages = useSelector((state) => state.clients.totalPages);
  const productsList = useSelector(
    (state) => state.productsServices.productsList?.items || []
  );

  const [rows, setRows] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [editingKey, setEditingKey] = useState(null);
  const [pendingAdd, setPendingAdd] = useState(false);
  const [originalRow, setOriginalRow] = useState(null);

  const input = {
    pageNo: currentPage,
    pageSize: 10,
    searchQuery: "",
  };

  useEffect(() => {
    dispatch(
      getProductsServicesAPI({ pageNo: null, pageSize: null, searchQuery: "" })
    );
    if (props.appointmentId) {
      dispatch(getAppointmentItemsList(parseInt(props.appointmentId)));
    } else {
      dispatch(GetWorkOrderItemsByWorkOrderIdApi(input, parseInt(workOrderId)));
    }
  }, [dispatch, workOrderId, currentPage, props.appointmentId]);

  useEffect(() => {
    const initialRows = workOrderItems.map((item) => ({
      ...item,
      key: item.id,
      isNew: false,
      description: item.description || "",
    }));

    if (pendingAdd && currentPage === 1) {
      const newRow = createEmptyRow();
      setRows([newRow, ...initialRows]);
      setEditingKey(newRow.key);
      setPendingAdd(false);
    } else {
      setRows(initialRows);
      if (!pendingAdd) {
        setEditingKey(null);
      }
    }
  }, [workOrderItems]);

  const createEmptyRow = () => ({
    key: Date.now(),
    productServiceId: null,
    productName: "",
    description: "",
    quantity: 1,
    unitPrice: 0,
    discount: 0,
    amount: 0,
    isNew: true,
  });

  const handleFieldChange = (index, field, value) => {
    const updatedRows = [...rows];
    const row = updatedRows[index];

    if (field === "productServiceId") {
      const selected = productsList.find((p) => p.id === value);
      row.productServiceId = value;
      row.productName = selected?.name || "";
      row.description = selected?.description || "";
      row.unitPrice = selected?.cost || 0;
      row.discount = selected?.discount || 0;
    } else {
      row[field] = value;
    }

    const qty = row.quantity || 1;
    const price = row.unitPrice || 0;
    const discount = row.discount || 0;
    const gross = qty * price;
    row.amount = parseFloat((gross - (gross * discount) / 100).toFixed(2));

    setRows(updatedRows);
  };

  const handleAdd = () => {
    if (editingKey !== null) {
      OpenNotificationWithIcon(
        "warning",
        "Common.Warning",
        "",
        "WorkOrder.PleaseSaveBeforeAdding"
      );
      return;
    }

    if (currentPage !== 1) {
      setPendingAdd(true);
      setCurrentPage(1);
    } else {
      const newRow = createEmptyRow();
      setRows([newRow, ...rows]);
      setEditingKey(newRow.key);
    }
  };
  const isDuplicate = (productServiceId, currentKey = null) =>
    rows.some(
      (row) =>
        row.productServiceId === productServiceId && row.key !== currentKey
    );

  const handleSave = (row, index) => {
    if (!row.productServiceId || !row.quantity || !row.unitPrice) {
      OpenNotificationWithIcon(
        "error",
        "Common.Error",
        "",
        "WorkOrder.FillAllRequiredFields"
      );
      return;
    }
    if (isDuplicate(row.productServiceId, row.key)) {
      OpenNotificationWithIcon(
        "error",
        "Common.Error",
        "",
        "WorkOrder.DuplicateProductService"
      );
      return;
    }
    const payload = {
      productServiceId: row.productServiceId,
      productName: row.productName,
      description: row.description,
      quantity: row.quantity,
      unitPrice: row.unitPrice,
      discount: row.discount,
      amount: row.amount,
      workOrderId: workOrderId,
    };
    const add = {
      appointmentId: props.appointmentId,
      productServiceId: row.productServiceId,
      productName: row.productName,
      description: row.description,
      quantity: row.quantity,
      unitPrice: row.unitPrice,
      discount: row.discount,
      amount: row.amount,
      workOrderId: workOrderId,
    };
    if (props.appointmentId) {
      if (row.id) {
        dispatch(updateAppointmentItem(parseInt(props.appointmentId), payload));
      } else {
        dispatch(addAppointmentItem(add, parseInt(props.appointmentId)));
      }
    } else {
      if (row.id) {
        dispatch(
          UpdateWorkOrderItemApi(parseInt(workOrderId), row.id, payload, input)
        );
      } else {
        dispatch(AddWorkOrderItemApi(parseInt(workOrderId), payload, input));
      }
    }
    setEditingKey(null);
  };

  const handleEdit = (key) => {
    if (editingKey !== null) {
      OpenNotificationWithIcon(
        "warning",
        "Common.Warning",
        "",
        "WorkOrder.Pleasesavethecurrentrowbeforeeditinganother"
      );
      return;
    }
    const rowToEdit = rows.find((r) => r.key === key);
    setOriginalRow({ ...rowToEdit });
    setEditingKey(key);
  };

  const handleDelete = (index) => {
    const rowToDelete = rows[index];
    if (props.appointmentId) {
      if (rowToDelete.id) {
        dispatch(
          deleteAppointmentItem(parseInt(rowToDelete.id), props.appointmentId)
        );
      }
    } else {
      if (rowToDelete.id) {
        dispatch(
          DeleteWorkOrderItemApi(parseInt(workOrderId), rowToDelete.id, input)
        );
      }
    }
  };

  const columns = [
    {
      title: "",
      dataIndex: "remove",
      render: (_, record, index) => {
        if (!record.isNew && record.id) {
          const disabled = !record?.isAdditionalItem || record?.isInvoiced;
          const tooltipTitle = disabled ? (
            <FormattedMessage id="WorkOrderDetails.TooltipCannoteditProduct" />
          ) : (
            ""
          );
          return (
            <Tooltip title={tooltipTitle}>
              <Popconfirm
                title={
                  <FormattedMessage id="AddEstimate.Areyousureyouwanttoremovethisitem" />
                }
                onConfirm={() => handleDelete(index)}
                okText={<FormattedMessage id="Common.Yes" />}
                cancelText={<FormattedMessage id="Common.No" />}
                disabled={disabled}
              >
                <span
                  style={{
                    display: "inline-block",
                    color: disabled ? "#ccc" : "red",
                    cursor: disabled ? "not-allowed" : "pointer",
                  }}
                >
                  <DeleteOutlined style={{ fontSize: 16 }} />
                </span>
              </Popconfirm>
            </Tooltip>
          );
        }
      },
    },
    {
      title: <FormattedMessage id="WorkOrder.ProductsServices" />,
      dataIndex: "productServiceId",
      render: (_, record, index) =>
        editingKey === record.key ? (
          <Select
            showSearch
            placeholder={intl.formatMessage({
              id: "WorkOrder.AddItem.SelectProductService",
            })}
            value={record.productServiceId}
            onChange={(val) =>
              handleFieldChange(index, "productServiceId", val)
            }
            style={{ width: "100%" }}
          >
            {productsList
              .filter(
                (item) =>
                  !workOrderItems.some(
                    (wo) =>
                      wo.productServiceId === item.id &&
                      wo.productServiceId !== record.productServiceId
                  )
              )
              .map((item) => (
                <Option key={item.id} value={item.id}>
                  {item.id} | {item.name}
                </Option>
              ))}
          </Select>
        ) : record.productServiceId && record.productName ? (
          `${record.productServiceId} | ${record.productName}`
        ) : (
          "-"
        ),
    },
    {
      title: <FormattedMessage id="AddEstimate.Description" />,
      dataIndex: "description",
      render: (_, record, index) =>
        editingKey === record.key ? (
          <Input.TextArea
            size="middle"
            rows={1}
            value={record.description}
            placeholder={intl.formatMessage({
              id: "Quote.Placeholder.EnterDescription",
            })}
            onChange={(e) =>
              handleFieldChange(index, "description", e.target.value)
            }
          />
        ) : (
          record.description || "-"
        ),
    },
    {
      title: <FormattedMessage id="WorkOrder.QtyHrs" />,
      dataIndex: "quantity",
      render: (_, record, index) =>
        editingKey === record.key ? (
          <InputNumber
            min={1}
            max={999}
            maxLength={3}
            parser={(value) => parseInt(value || "0", 10)}
            value={record.quantity}
            onChange={(val) => handleFieldChange(index, "quantity", val)}
          />
        ) : (
          record.quantity
        ),
    },
    {
      title: <FormattedMessage id="WorkOrderDetails.UnitPrice" />,
      dataIndex: "unitPrice",
      render: (_, record, index) =>
        editingKey === record.key ? (
          <InputNumber
            min={0}
            maxLength={10}
            value={record.unitPrice}
            formatter={(val) => `$${val}`}
            onChange={(val) => handleFieldChange(index, "unitPrice", val)}
          />
        ) : (
          `$${record.unitPrice}`
        ),
    },
    {
      title: <FormattedMessage id="WorkOrder.Discount" />,
      dataIndex: "discount",
      render: (_, record, index) =>
        editingKey === record.key ? (
          <InputNumber
            min={0}
            max={100}
            maxLength={4}
            parser={(value) => {
              const parsed = parseInt(value?.replace("%", "") || "0", 10);
              return Math.min(parsed, 100);
            }}
            formatter={(val) => `${val}%`}
            value={record.discount}
            onChange={(val) => handleFieldChange(index, "discount", val)}
          />
        ) : (
          `${record.discount}%`
        ),
    },
    {
      title: <FormattedMessage id="WorkOrder.Amount" />,
      dataIndex: "amount",
      render: (text) => `$${text || 0}`,
    },
    {
      title: <FormattedMessage id="Common.Action" />,
      dataIndex: "action",
      render: (_, record, index) =>
        editingKey === record.key ? (
          <span>
            <Button
              type="primary"
              onClick={() => handleSave(record, index)}
              style={{ marginRight: 8 }}
            >
              {<FormattedMessage id="Common.Save" />}
            </Button>
            <Button
              onClick={() => {
                if (record.isNew) {
                  const updated = [...rows];
                  updated.splice(index, 1);
                  setRows(updated);
                } else if (originalRow) {
                  const updated = [...rows];
                  updated[index] = { ...originalRow };
                  setRows(updated);
                }
                setEditingKey(null);
                setOriginalRow(null);
              }}
              danger
            >
              {<FormattedMessage id="Common.Cancel" />}
            </Button>
          </span>
        ) : (
          <Tooltip
            title={
              !record?.isAdditionalItem || record?.isInvoiced ? (
                <FormattedMessage id="WorkOrderDetails.TooltipCannoteditProduct" />
              ) : (
                ""
              )
            }
          >
            <Button
              icon={<EditOutlined />}
              onClick={() => handleEdit(record.key)}
              disabled={!record?.isAdditionalItem || record?.isInvoiced}
            />
          </Tooltip>
        ),
    },
  ];

  return (
    <>
      <Row justify="space-between" align="middle">
        <Col>
          <h5>
            <FormattedMessage id="Workorder.Products" />
          </h5>
        </Col>
        <Col>
          <Button type="primary" onClick={handleAdd}>
            <FormattedMessage id="Workorder.AddProduct" />
          </Button>
        </Col>
      </Row>

      <Table
        bordered
        className="mt-10"
        scroll={{ x: 1100 }}
        dataSource={rows}
        columns={columns}
        pagination={false}
        rowKey="key"
      />
    </>
  );
}

export default WorkOrderProducts;
